import axios from 'axios';
import { supabase } from '../utils/supabase';
import type { Cadena } from '../utils/cadena';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface DocumentScores {
  semantic: number;
  lexical: number;
  combined: number;
}

export interface TypeBreakdown {
  total: number;
  oficio: number;
  circular: number;
  resolucion: number;
}

export interface Document {
  id: string;
  document_id: string;
  tipo: 'oficio' | 'circular' | 'resolucion';
  year: number;
  numero: string;
  fecha_publicacion: string;
  fecha_emision?: string;
  titulo: string;
  resumen: string;
  ai_summary?: string;
  contenido_extraido?: string;
  articulos_relevantes?: string[];
  pdf_url?: string;
  source_url: string;
  storage_path?: string;
  file_hash?: string;
  metadata?: any;
  materia?: string;
  source?: string;
  institution_code: string;
  created_at: string;
  updated_at?: string;
  is_favorite?: boolean;
  // Search relevance scores (only present in hybrid search results)
  scores?: DocumentScores;
}

export interface DocumentsResponse {
  success: boolean;
  data: Document[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    // Type breakdown counts from backend (hybrid search only)
    typeBreakdown?: TypeBreakdown;
  };
}

export interface DocumentResponse {
  success: boolean;
  data: Document;
}

// ──────────────────────────────────────
// Passage / Source Panel types (citation trust — text-level highlight)
// ──────────────────────────────────────

export interface PassageChunk {
  index: number;
  content: string;
}

export interface PassageDocumentSummary {
  id: string;
  titulo: string;
  tipo: string;
  numero?: string | null;
  year: number;
  fecha_publicacion: string;
  source_url: string;
}

export interface PassageResponse {
  document: PassageDocumentSummary;
  best_chunk_index: number;
  chunks: PassageChunk[];
  total_chunks: number;
  chunk_indexes: number[];
  no_chunks?: boolean;
}

export interface SearchParams {
  page?: number;
  limit?: number;
  tipo?: string;
  year?: number;
  materia?: string;
  search?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
  institution?: string;
  // Legislation filters (SII only)
  ley_codigo?: string;
  convenio_pais?: string;
  otro_codigo?: string;
  articulos?: string;
}

/** Fila de GET /documents/favorites: lo que muestran la lista y su ficha (sin el texto completo), más la fecha de guardado. */
export interface FavoriteDocument {
  id: string;
  institution_code: string;
  tipo: string;
  numero: string | null;
  year: number | null;
  titulo: string;
  fecha_publicacion: string | null;
  materia: string | null;
  resumen?: string | null;
  articulos_relevantes?: string[] | null;
  source_url?: string | null;
  favorited_at: string;
}

/** Fila de GET /documents/:id/related. */
export interface RelatedDocument {
  id: string;
  institution_code: string;
  tipo: string;
  numero: string | null;
  year: number | null;
  titulo: string;
  resumen: string | null;
  fecha_publicacion: string | null;
  materia: string | null;
}

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
};

export const documentsService = {
  async getAll(params: SearchParams = {}): Promise<DocumentsResponse> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/documents`, { params, headers });
    return response.data;
  },

  async getById(id: string): Promise<DocumentResponse> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/documents/${id}`, { headers });
    return response.data;
  },

  async search(query: string, filters: Omit<SearchParams, 'search'> = {}): Promise<DocumentsResponse> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/documents/search`, {
      params: { q: query, ...filters },
      headers
    });
    return response.data;
  },

  async semanticSearch(query: string, filters: Omit<SearchParams, 'search'> = {}): Promise<DocumentsResponse> {
    const headers = await getAuthHeaders();
    const requestedLimit = filters.limit || 50;
    const response = await axios.post(`${API_URL}/search/semantic`, {
      query: query,
      institution_code: filters.institution,
      tipo: filters.tipo,
      year: filters.year,
      materia: filters.materia,
      // Legislation filters
      ley_codigo: filters.ley_codigo,
      convenio_pais: filters.convenio_pais,
      otro_codigo: filters.otro_codigo,
      articulos: filters.articulos,
      limit: requestedLimit
    }, { headers });
    const totalResults = response.data.results_count || 0;
    return {
      success: response.data.success,
      data: response.data.results || [],
      pagination: {
        total: totalResults,
        page: filters.page || 1,
        limit: requestedLimit,
        totalPages: Math.ceil(totalResults / requestedLimit)
      }
    };
  },

  async hybridSearch(query: string, filters: Omit<SearchParams, 'search'> = {}): Promise<DocumentsResponse> {
    const headers = await getAuthHeaders();
    const requestedLimit = filters.limit || 50;
    const requestedPage = filters.page || 1;
    const response = await axios.post(`${API_URL}/search/hybrid`, {
      query: query,
      institution_code: filters.institution,
      tipo: filters.tipo,
      year: filters.year,
      materia: filters.materia,
      // Legislation filters
      ley_codigo: filters.ley_codigo,
      convenio_pais: filters.convenio_pais,
      otro_codigo: filters.otro_codigo,
      articulos: filters.articulos,
      // Pagination parameters
      page: requestedPage,
      limit: requestedLimit,
      // Sorting parameters
      sort: filters.sortBy || 'relevance',
      order: filters.order || 'desc'
    }, { headers });
    // Use pagination from backend response if available
    return {
      success: response.data.success,
      data: response.data.results || [],
      pagination: response.data.pagination || {
        total: response.data.results_count || 0,
        page: requestedPage,
        limit: requestedLimit,
        totalPages: Math.ceil((response.data.results_count || 0) / requestedLimit)
      }
    };
  },

  /** Favoritos del usuario, del más reciente al más antiguo, con su fecha de guardado (H3.1). */
  async getFavorites(limit = 100, offset = 0): Promise<{ data: FavoriteDocument[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/documents/favorites`, { params: { limit, offset }, headers });
    return response.data;
  },

  /** Misma institución, tipo y materia, año cercano (GET /documents/:id/related, solo columnas de fila). */
  async getRelated(documentId: string, limit = 6): Promise<RelatedDocument[]> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/documents/${documentId}/related`, { params: { limit }, headers });
    return response.data.data || [];
  },

  /** Cadena del criterio (GET /documents/:id/cadena, DESIGN.md §11.7): vigencia, normas de la cabecera, citas resueltas. */
  async getCadena(documentId: string): Promise<Cadena> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API_URL}/documents/${documentId}/cadena`, { headers });
    const d = response.data?.data || {};
    return {
      vigencia: { modificado_por: d.vigencia?.modificado_por || null },
      normas: Array.isArray(d.normas) ? d.normas : [],
      citado_por: Array.isArray(d.citado_por) ? d.citado_por : [],
      cita_a: Array.isArray(d.cita_a) ? d.cita_a : [],
      original_url: d.original_url || null,
    };
  },

  async toggleFavorite(documentId: string): Promise<{ success: boolean; isFavorite: boolean }> {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_URL}/documents/${documentId}/favorite`, {}, { headers });
    return response.data;
  },

  async chatWithDocument(documentId: string, question: string, maxChunks: number = 5): Promise<any> {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${API_URL}/documents/${documentId}/chat`, {
      question,
      maxChunks
    }, { headers });
    return response.data;
  },

  /**
   * Fetches the chunk(s) that best match a citation excerpt (or a specific
   * chunk neighborhood via `around`), for the Source Panel's text-level
   * passage highlight. Backend does simple word-overlap scoring — no LLM call.
   */
  async getPassage(documentId: string, opts: { q?: string; around?: number } = {}): Promise<PassageResponse> {
    const headers = await getAuthHeaders();
    const params: Record<string, string | number> = {};
    if (opts.q) params.q = opts.q;
    if (opts.around !== undefined) params.around = opts.around;
    const response = await axios.get(`${API_URL}/documents/${documentId}/passage`, { params, headers });
    return response.data.data as PassageResponse;
  }
};

export default documentsService;
